// In-editor combat playtest: keyboard/mouse input, wired straight onto the
// sim's own `PlayerInputState` (the same class `sstk-sunder-web` drives) so
// movement/aim/fire feel exactly like the real client, no reimplementation.
import { PlayerInputState, type WeaponSlot } from '@sstk/sunder-sim';

const SLOT_KEYS: Record<string, WeaponSlot> = {
  Digit1: 'melee',
  Digit2: 'secondary',
  Digit3: 'primary',
};

/**
 * Owns one `PlayerInputState` and keeps it in sync with keyboard, mouse-move
 * (pointer-lock deltas) and mouse-button events on a target element. Call
 * `dispose()` on unmount/exit - it removes every listener and releases
 * pointer lock if this controller holds it.
 */
export class PlaytestInputController {
  readonly state = new PlayerInputState();
  private readonly target: HTMLElement;
  private readonly heldKeys = new Set<string>();
  private disposed = false;

  constructor(target: HTMLElement) {
    this.target = target;
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('mousemove', this.onMouseMove);
    target.addEventListener('mousedown', this.onMouseDown);
    window.addEventListener('mouseup', this.onMouseUp);
    target.addEventListener('contextmenu', this.onContextMenu);
  }

  /** Request pointer lock on the target element (call from a user gesture). */
  requestLock(): void {
    this.target.requestPointerLock?.();
  }

  get locked(): boolean {
    return document.pointerLockElement === this.target;
  }

  private updateMoveAxes(): void {
    const forward = this.heldKeys.has('KeyW') ? -1 : this.heldKeys.has('KeyS') ? 1 : 0;
    const strafe = this.heldKeys.has('KeyA') ? -1 : this.heldKeys.has('KeyD') ? 1 : 0;
    this.state.moveZ = forward;
    this.state.moveX = strafe;
  }

  private readonly onKeyDown = (event: KeyboardEvent): void => {
    // Escape-to-exit is handled by a separate, always-on listener in
    // PlaytestOverlay (it must work even before the player ever locks the
    // pointer), so this controller only needs to act while locked.
    if (!this.locked) return;
    if (event.repeat) {
      // Still consume movement-key repeats so held-key OS auto-repeat never
      // re-fires an edge-triggered action below.
      if (event.code === 'KeyW' || event.code === 'KeyA' || event.code === 'KeyS' || event.code === 'KeyD') return;
    }
    this.heldKeys.add(event.code);
    switch (event.code) {
      case 'KeyW':
      case 'KeyA':
      case 'KeyS':
      case 'KeyD':
        this.updateMoveAxes();
        break;
      case 'Space':
        this.state.jumpPressed = true;
        event.preventDefault();
        break;
      case 'KeyR':
        this.state.reloadPressed = true;
        break;
      case 'ShiftLeft':
      case 'ShiftRight':
        this.state.walking = true;
        break;
      case 'ControlLeft':
      case 'ControlRight':
      case 'KeyC':
        this.state.crouching = true;
        break;
      default: {
        const slot = SLOT_KEYS[event.code];
        if (slot) this.state.wantSlot = slot;
      }
    }
  };

  private readonly onKeyUp = (event: KeyboardEvent): void => {
    this.heldKeys.delete(event.code);
    switch (event.code) {
      case 'KeyW':
      case 'KeyA':
      case 'KeyS':
      case 'KeyD':
        this.updateMoveAxes();
        break;
      case 'ShiftLeft':
      case 'ShiftRight':
        this.state.walking = false;
        break;
      case 'ControlLeft':
      case 'ControlRight':
      case 'KeyC':
        this.state.crouching = false;
        break;
    }
  };

  private readonly onMouseMove = (event: MouseEvent): void => {
    if (!this.locked) return;
    this.state.addMouseMovement(event.movementX, event.movementY);
  };

  private readonly onMouseDown = (event: MouseEvent): void => {
    if (!this.locked) {
      this.requestLock();
      return;
    }
    if (event.button === 0) this.state.firing = true;
    if (event.button === 2) this.state.aiming = true;
  };

  private readonly onMouseUp = (event: MouseEvent): void => {
    if (event.button === 0) this.state.firing = false;
    if (event.button === 2) this.state.aiming = false;
  };

  private readonly onContextMenu = (event: Event): void => {
    // The right mouse button drives aim-down-sights, not a context menu.
    event.preventDefault();
  };

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('mousemove', this.onMouseMove);
    this.target.removeEventListener('mousedown', this.onMouseDown);
    window.removeEventListener('mouseup', this.onMouseUp);
    this.target.removeEventListener('contextmenu', this.onContextMenu);
    this.state.clear();
    if (this.locked) document.exitPointerLock?.();
  }
}
